import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';

import { AuthService } from '../../services/auth.service';
import { PropertyService } from '../../services/property.service';
import { LesseeService } from '../../services/lessee.service';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss']
})
export class InvoicesComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()
  property;

  showUnpaid = true;
  showPaid = false;
  searchText = '';
  loading = true;

  displayedColumns: string[] = ['dueDate', 'amount', 'status', 'remainingBalance', 'actions'];
  dataSource = new MatTableDataSource<any>();
  invoices: any[] = [];
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  // Pagination properties
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;

  constructor(private propertyService: PropertyService,
              public authService: AuthService,
              private lesseeService: LesseeService,
              private router: Router) { }

  ngOnInit() {
    this.loadInvoices();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['property'] && !changes['property'].isFirstChange()) {
      this.loadInvoices();
    }
  }

  private loadInvoices() {
    this.loading = true;
    let promise: Promise<any>;
    if (this.authService.isAdmin()) {
      if (!this.property) {
        promise = Promise.resolve([]);
      } else {
        promise = this.propertyService.getInvoices(this.property, this.currentPage, this.pageSize);
      }
    } else {
      // Lessee invoices - for now keep non-paginated (can be updated later)
      promise = this.lesseeService.getInvoices();
    }
    promise.then(res => {
      this.loading = false;
      
      // Handle paginated response
      if (res && res.data && Array.isArray(res.data)) {
        // IMPORTANT: Replace invoices with ONLY the current page's data
        // Preserve statementId and ensure payments are properly mapped
        this.invoices = res.data.map(inv => {
          // Preserve statementId (handle both camelCase and snake_case)
          if (inv.statement_id && !inv.statementId) {
            inv.statementId = inv.statement_id;
          }
          // Ensure payments have statementId mapped correctly
          if (inv.payments && Array.isArray(inv.payments)) {
            inv.payments = inv.payments.map((p: any) => {
              if (p.statement_id && !p.statementId) {
                p.statementId = p.statement_id;
              }
              return p;
            });
          }
          return inv;
        });
        this.totalItems = res.total || 0;
        this.currentPage = res.page || 1;
        this.pageSize = res.pageSize || 10;
        console.log(`[Invoices] Loaded page ${this.currentPage}, showing ${this.invoices.length} of ${this.totalItems} total invoices`);
      } else {
        // Fallback for non-paginated response (e.g. lessee invoices)
        this.invoices = Array.isArray(res) ? res.map(inv => {
          // Preserve statementId (handle both camelCase and snake_case)
          if (inv.statement_id && !inv.statementId) {
            inv.statementId = inv.statement_id;
          }
          // Ensure payments have statementId mapped correctly
          if (inv.payments && Array.isArray(inv.payments)) {
            inv.payments = inv.payments.map((p: any) => {
              if (p.statement_id && !p.statementId) {
                p.statementId = p.statement_id;
              }
              return p;
            });
          }
          return inv;
        }) : [];
        this.totalItems = this.invoices.length;
      }
      
      // Only sort if not paginated (server already sorts)
      if (!res || !res.data) {
        this.invoices.sort((a, b) => {
          const da = new Date(a.dueDate).getTime();
          const db = new Date(b.dueDate).getTime();
          return da - db;
        });
      }
      
      this.updateDataSource();
    }).catch(() => {
      this.loading = false;
      this.invoices = [];
      this.totalItems = 0;
      this.updateDataSource();
    });
  }

  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadInvoices();
  }

  reload() {
    this.propertyService.clearInvoices(this.property);
    this.currentPage = 1; // Reset to first page on reload
    this.loadInvoices();
  }

  applyFilter() {
    this.updateDataSource();
  }

  toggleChanged() {
    this.updateDataSource();
  }

  clearFilters() {
    this.searchText = '';
    this.showUnpaid = true;
    this.showPaid = true;
    this.updateDataSource();
  }

  view(invoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (this.authService.isAdmin()) {
      this.router.navigate(['/invoice', invoice.id, 'pdf'], { queryParams: { propertyId: this.property.id } });
    } else {
      this.router.navigate(['/invoice', invoice.id, 'pdf']);
    }
  }

  download(invoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    let promise: Promise<Blob>;
    if (this.authService.isAdmin()) {
      const prop = this.property || (invoice.property ? invoice.property : (invoice.propertyId ? { id: invoice.propertyId } : null));
      if (!prop || !prop.id) {
        // fall back to lessee style if no prop context (shouldn't happen for admin)
        promise = this.lesseeService.getInvoicePdf(invoice.id);
      } else {
        promise = this.propertyService.getInvoicePdf(prop, invoice.id);
      }
    } else {
      promise = this.lesseeService.getInvoicePdf(invoice.id);
    }
    promise.then((blob: Blob) => {
      const safeDue = (invoice.dueDate || '').toString().replace(/[^0-9A-Za-z.-]/g, '');
      const fileName = safeDue ? `invoice-${safeDue}-${invoice.id}.pdf` : `invoice-${invoice.id}.pdf`;
      this.saveBlobReliably(blob, fileName);
    }).catch((err) => {
      console.error('Failed to download invoice PDF:', err);
      alert('Unable to download the invoice PDF. Please try again.');
    });
  }

  /** Share-first save for iOS reliability (named File via Web Share -> Save to Files), with blob fallback. */
  private saveBlobReliably(blob: Blob, fileName: string) {
    const nav: any = (typeof navigator !== 'undefined') ? navigator : null;
    const tryWebShareFile = async (): Promise<boolean> => {
      if (!nav || typeof nav.share !== 'function') {
        return false;
      }
      try {
        const file = new File([blob], fileName, { type: 'application/pdf', lastModified: Date.now() });
        const canShareFiles = !nav.canShare || nav.canShare({ files: [file] });
        if (canShareFiles) {
          await nav.share({ files: [file] });
          return true;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return true;
        }
      }
      return false;
    };
    tryWebShareFile().then((did) => {
      if (did) {
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    });
  }

  generatePdf(invoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!this.authService.isAdmin()) {
      return; // Lessees cannot generate PDFs
    }
    this.propertyService.generateInvoicePdf(this.property, invoice.id).then((res: any) => {
      if (res) {
        // Update the invoice in the list
        const index = this.invoices.findIndex(inv => inv.id === invoice.id);
        if (index !== -1) {
          this.invoices[index] = res;
          this.updateDataSource();
        }
      }
    }).catch((error) => {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    });
  }

  deletePdf(invoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!this.authService.isAdmin()) {
      return; // Lessees cannot delete PDFs
    }
    if (!confirm('Are you sure you want to delete this PDF? You can regenerate it later.')) {
      return;
    }
    this.propertyService.deleteInvoicePdf(this.property, invoice.id).then((res: any) => {
      if (res) {
        // Update the invoice in the list
        const index = this.invoices.findIndex(inv => inv.id === invoice.id);
        if (index !== -1) {
          this.invoices[index] = res;
          this.updateDataSource();
        }
      }
    }).catch((error) => {
      console.error('Error deleting PDF:', error);
      alert('Failed to delete PDF. Please try again.');
    });
  }

  hasStatementId(item: any): boolean {
    if (!item) return false;
    // Check both camelCase and snake_case, and also check for truthy values (not null, undefined, or empty string)
    const statementId = item.statementId || item.statement_id;
    const hasId = !!(statementId && statementId !== null && statementId !== undefined && statementId !== '');
    
    // Also check if invoice has payments with statement_id (invoices created from statements often have statement-linked payments)
    if (!hasId && item.payments && Array.isArray(item.payments)) {
      const hasStatementPayment = item.payments.some((p: any) => {
        const paymentStatementId = p.statementId || p.statement_id;
        return !!(paymentStatementId && paymentStatementId !== null && paymentStatementId !== undefined && paymentStatementId !== '');
      });
      if (hasStatementPayment) {
        return true;
      }
    }
    
    return hasId;
  }

  delete(invoice, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!this.authService.isAdmin()) {
      return;
    }
    if (this.hasStatementId(invoice)) {
      alert('Cannot delete invoice created from a statement. Remove the statement instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this invoice?')) {
      return;
    }
    this.propertyService.deleteInvoice(this.property, invoice).then(() => {
      this.reload();
    }).catch((error) => {
      console.error('Error deleting invoice:', error);
      const errorMessage = (error && error.error && error.error.error) ? error.error.error : (error.message || 'Unknown error');
      if (errorMessage.includes('statement')) {
        alert('Cannot delete invoice created from a statement. Remove the statement instead.');
      } else {
        alert('Failed to delete invoice. Please try again.');
      }
    });
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
    if (this.paginator) {
      // For server-side pagination, we handle pagination events manually
      this.paginator.page.subscribe(event => {
        this.onPageChange(event);
      });
    }
  }

  private updateDataSource() {
    const filtered = this.invoices.filter(inv => {
      if (inv.remainingBalance > 0 && this.showUnpaid) {
        return true;
      }
      if (inv.remainingBalance <= 0 && this.showPaid) {
        return true;
      }
      return false;
    });
    this.dataSource.data = filtered;
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
    this.dataSource.filterPredicate = (data, filter) => {
      const dataStr = `${data.dueDate} ${data.amount} ${data.status} ${data.remainingBalance}`.toLowerCase();
      return dataStr.includes(filter);
    };
    this.dataSource.filter = (this.searchText || '').trim().toLowerCase();
  }
}
